import { motion } from "framer-motion";
import { Star, Quote, Linkedin } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { TESTIMONIALS } from "@/data/content";

export const Testimonials = () => {
  return (
    <section className="relative overflow-hidden bg-background pt-20 pb-0 lg:pt-28">
      {/* Light orange glow */}
      <div className="pointer-events-none absolute -left-24 top-1/3 h-64 w-64 rounded-full bg-primary/8 blur-[80px]" />

      <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
            Success Stories
          </span>
          <h2 className="section-title mt-4 text-foreground">
            Real People,<br /> Real Transformations
          </h2>
          <p className="mt-4 text-base text-muted-foreground md:text-lg">
            Recommendations from executives & mid to senior professionals, rated <span className="font-semibold text-foreground">4.9 / 5</span>
          </p>
        </div>

        <Carousel opts={{ align: "start", loop: true }} className="mt-14">
          <CarouselContent className="-ml-5">
            {TESTIMONIALS.map((t) => (
              <CarouselItem key={t.name} className="pl-5 sm:basis-1/2 lg:basis-1/3">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5 }}
                  className="flex h-full flex-col justify-between rounded-2xl border border-border bg-white p-7 shadow-sm transition-all hover:border-primary/40 hover:shadow-md"
                >
                  <div>
                    <div className="flex items-start justify-between">
                      <Quote className="h-8 w-8 text-primary/30" />
                      <span className="flex text-primary">
                        {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
                      </span>
                    </div>
                    <p className="mt-4 text-sm leading-relaxed text-foreground/80">"{t.quote}"</p>
                  </div>
                  <div className="mt-6 flex items-center justify-between border-t border-border pt-5">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-11 w-11 border-2 border-primary/20">
                        <AvatarFallback className="bg-gradient-primary text-xs font-bold text-white">
                          {t.initials}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="text-sm font-bold text-foreground">{t.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">{t.role}</p>
                      </div>
                    </div>
                    {t.linkedin && (
                      <a
                        href={t.linkedin}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={`${t.name}'s LinkedIn recommendation`}
                        className="rounded-full bg-[#0a66c2]/10 p-2 text-[#0a66c2] hover:bg-[#0a66c2] hover:text-white transition-colors shrink-0"
                      >
                        <Linkedin className="h-4 w-4" />
                      </a>
                    )}
                  </div>
                </motion.div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-10 flex justify-center gap-3">
            <CarouselPrevious className="static translate-y-0 border-border bg-white hover:bg-secondary hover:border-primary/50" />
            <CarouselNext className="static translate-y-0 border-border bg-white hover:bg-secondary hover:border-primary/50" />
          </div>
        </Carousel>
      </div>
    </section>
  );
};
